import { stepNeedsTool, type DraftStep, type ValidationIssue } from "@zworks/console-contracts";
import {
  AlertTriangle,
  Bot,
  Box,
  Clock,
  CornerDownLeft,
  Cpu,
  GripVertical,
  Rocket,
  ShieldAlert,
  Sparkles,
  Users,
  Wrench,
  Zap,
} from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { type NodeLayout } from "@/lib/flow-layout";
import { cn } from "@/lib/utils";
import type { CapabilityOption } from "./step-inspector";

export interface CanvasNodeProps {
  step: DraftStep;
  allSteps: DraftStep[];
  layout: NodeLayout;
  isSelected: boolean;
  issues: ValidationIssue[];
  tools: CapabilityOption[];
  skills: CapabilityOption[];
  canEdit: boolean;
  onSelect: () => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  dragProps: Record<string, unknown>;
  onNodeMouseDown?: (e: React.MouseEvent, id: string) => void;
  connectingFrom: string | null;
  onStartConnection?: (id: string, kind: "approve" | "reject" | "next") => void;
  onFinishConnection?: (id: string) => void;
}

function renderNodeIcon(kind: string, isGate: boolean) {
  const icon = isGate
    ? ShieldAlert
    : kind.toLowerCase().includes("deploy") || kind.toLowerCase().includes("release")
      ? Rocket
      : kind.toLowerCase().includes("tool") || kind.toLowerCase().includes("action")
        ? Wrench
        : kind.toLowerCase().includes("plan") || kind.toLowerCase().includes("trigger")
          ? Sparkles
          : Bot;
  const NodeIcon = icon;
  return <NodeIcon className="size-4 shrink-0" aria-hidden="true" />;
}

function getNodeTheme(kind: string, isGate: boolean) {
  if (isGate) {
    return {
      bar: "bg-warning/15 text-warning border-warning/30",
      accent: "text-warning",
      tag: "Gate",
    };
  }
  const lower = kind.toLowerCase();
  if (lower.includes("deploy")) {
    return {
      bar: "bg-primary/15 text-accent-text border-primary/30",
      accent: "text-accent-text",
      tag: "Deploy",
    };
  }
  if (lower.includes("tool")) {
    return {
      bar: "bg-info/15 text-info border-info/30",
      accent: "text-info",
      tag: "Tool",
    };
  }
  return {
    bar: "bg-secondary text-secondary-foreground border-border/40",
    accent: "text-accent-text",
    tag: "Task",
  };
}

export function CanvasNode({
  step,
  allSteps,
  layout,
  isSelected,
  issues,
  tools,
  skills,
  canEdit,
  onSelect,
  onKeyDown,
  dragProps,
  onNodeMouseDown,
  connectingFrom,
  onStartConnection,
  onFinishConnection,
}: CanvasNodeProps) {
  const theme = getNodeTheme(step.kind, step.gate);
  const rejectTarget = step.rejectRoute ? allSteps.find((s) => s.id === step.rejectRoute) : null;
  const boundTool = tools.find((tool) => tool.id === step.toolRef);
  const boundSkill = skills.find((skill) => skill.id === step.skillRef);
  const hasRequiredTool = !stepNeedsTool(step) || boundTool !== undefined;

  return (
    <div
      role="listitem"
      style={{
        position: "absolute",
        left: `${layout.position.x}px`,
        top: `${layout.position.y}px`,
        width: `${layout.position.width}px`,
      }}
      className="group select-none"
    >
      {/* Input handle (top): drag target that completes a connection */}
      <div
        role="button"
        tabIndex={canEdit ? 0 : -1}
        aria-label={`Connect to ${step.name}`}
        onMouseUp={(event) => {
          event.stopPropagation();
          onFinishConnection?.(step.id);
        }}
        onKeyDown={(event) => {
          if ((event.key === "Enter" || event.key === " ") && connectingFrom !== null) {
            event.preventDefault();
            event.stopPropagation();
            onFinishConnection?.(step.id);
          }
        }}
        style={{
          left: `${layout.inputPort.x - layout.position.x}px`,
        }}
        className={cn(
          "absolute -top-2 z-20 size-4 -translate-x-1/2 rounded-full border-2 border-primary bg-background shadow-sm transition-transform flex items-center justify-center",
          "hover:scale-125 hover:border-primary-hover",
          connectingFrom !== null && connectingFrom !== step.id && "scale-125 border-dashed",
        )}
      >
        <div className="size-1.5 rounded-full bg-primary" />
      </div>

      {/* Output handle (bottom): drag from here to another node to connect */}
      {!step.gate ? (
        <div
          role="button"
          tabIndex={canEdit ? 0 : -1}
          aria-label={`Connect from ${step.name}`}
          onMouseDown={(event) => {
            event.stopPropagation();
            onStartConnection?.(step.id, "next");
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.stopPropagation();
              onStartConnection?.(step.id, "next");
            }
          }}
          style={{
            left: `${layout.outputPort.x - layout.position.x}px`,
          }}
          className={cn(
            "absolute -bottom-2 z-20 size-4 -translate-x-1/2 rounded-full border-2 border-background shadow-sm transition-transform cursor-crosshair",
            "bg-primary hover:scale-125 hover:bg-primary-hover",
            connectingFrom === step.id && "scale-125",
          )}
        />
      ) : (
        <>
          {/* Gate Approve Port */}
          <div
            role="button"
            tabIndex={canEdit ? 0 : -1}
            aria-label={`Connect approval from ${step.name}`}
            onMouseDown={(event) => {
              event.stopPropagation();
              onStartConnection?.(step.id, "approve");
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                onStartConnection?.(step.id, "approve");
              }
            }}
            style={{
              left: `${(layout.approvePort ?? layout.outputPort).x - layout.position.x}px`,
            }}
            className={cn(
              "absolute -bottom-2 z-20 size-4 -translate-x-1/2 rounded-full border-2 border-background shadow-sm transition-transform cursor-crosshair",
              "bg-success hover:scale-125 hover:brightness-110",
              connectingFrom === step.id && "scale-125",
            )}
            title="Approved: Continue"
          />
          {/* Gate Reject Port */}
          <div
            role="button"
            tabIndex={canEdit ? 0 : -1}
            aria-label={`Connect rejection from ${step.name}`}
            onMouseDown={(event) => {
              event.stopPropagation();
              onStartConnection?.(step.id, "reject");
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                onStartConnection?.(step.id, "reject");
              }
            }}
            style={{
              left: `${(layout.rejectPort ?? layout.outputPort).x - layout.position.x}px`,
            }}
            className={cn(
              "absolute -bottom-2 z-20 size-4 -translate-x-1/2 rounded-full border-2 border-background shadow-sm transition-transform cursor-crosshair",
              "bg-danger hover:scale-125 hover:brightness-110",
              connectingFrom === step.id && "scale-125",
            )}
            title="Rejected: Reroute"
          />
        </>
      )}

      {/* Main Node Card (outer border with selection and draggable header) */}
      <div
        onClick={onSelect}
        onKeyDown={onKeyDown}
        className={cn(
          "relative flex w-full flex-col overflow-hidden rounded-xl border bg-card text-left transition-all duration-150 outline-none",
          step.gate ? "min-h-[150px]" : "min-h-[105px]",
          "shadow-sm hover:shadow-md hover:border-primary/50",
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          isSelected && "border-primary ring-2 ring-primary/50 shadow-lg",
          issues.length > 0 && "border-danger/60 ring-1 ring-danger/30",
        )}
      >
        {/* Node Header (ComfyUI / Langflow header bar - draggable handle) */}
        <div
          onMouseDown={(e) => {
            if (canEdit && e.button === 0) {
              onNodeMouseDown?.(e, step.id);
            }
          }}
          className={cn(
            "flex items-center justify-between border-b px-3 py-2 text-xs font-medium tracking-tight cursor-grab active:cursor-grabbing",
            theme.bar,
          )}
        >
          <div className="flex items-center gap-1.5 min-w-0">
            {canEdit ? (
              <GripVertical
                className="size-3.5 text-muted-foreground shrink-0 opacity-60 group-hover:opacity-100"
                aria-hidden="true"
              />
            ) : null}
            {renderNodeIcon(step.kind, step.gate)}
            <span className="font-semibold text-foreground truncate">{step.name}</span>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {step.podConfig?.parallel && step.podConfig.parallel > 1 ? (
              <span className="flex items-center gap-0.5 text-xs font-bold text-accent-text bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded">
                <Zap className="size-2.5" aria-hidden="true" />×{step.podConfig.parallel} Pods
              </span>
            ) : null}
            <span className="text-xs font-bold tabular-nums text-muted-foreground bg-background/80 px-1.5 py-0.5 rounded border border-border/40">
              #{step.position}
            </span>
            <span className="text-xs text-muted-foreground">({step.kind})</span>
          </div>
        </div>

        {/* Node Body & Details (Accessible click button) */}
        <button
          type="button"
          tabIndex={0}
          aria-label={step.name}
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          {...dragProps}
          className="flex flex-col gap-1.5 p-3 text-xs w-full text-left bg-transparent border-0 outline-none"
        >
          {/* Tool binding or task info */}
          <div className="flex items-center justify-between text-muted-foreground w-full">
            <span className="flex items-center gap-1.5 truncate">
              <Wrench className="size-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">
                {boundTool
                  ? `Tool: ${boundTool.label}`
                  : step.toolRef
                    ? "Unavailable tool"
                    : stepNeedsTool(step)
                      ? "Tool required · not bound"
                      : "No tool required"}
              </span>
            </span>

            {boundSkill ? (
              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                Skill: {boundSkill.label}
              </Badge>
            ) : null}
            {!hasRequiredTool ? (
              <Badge variant="warning" className="text-xs px-1.5 py-0">
                Tool needed
              </Badge>
            ) : null}
            {step.gate ? (
              <Badge variant="warning" className="text-xs px-1.5 py-0">
                Gate
              </Badge>
            ) : null}
          </div>

          {/* Pod Container Spec Chip (Container Image & Resource Specs) */}
          {!step.gate &&
          (step.podConfig?.image || step.podConfig?.cpu || step.podConfig?.memory) ? (
            <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
              {step.podConfig?.image ? (
                <span className="inline-flex items-center gap-1 text-xs text-foreground bg-muted/60 border border-border/60 rounded px-1.5 py-0.5 max-w-[190px] truncate">
                  <Box className="size-3 text-accent-text shrink-0" aria-hidden="true" />
                  <span className="truncate">{step.podConfig.image}</span>
                </span>
              ) : null}
              {step.podConfig?.cpu || step.podConfig?.memory ? (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted/40 border border-border/40 rounded px-1.5 py-0.5">
                  <Cpu className="size-2.5 text-muted-foreground" aria-hidden="true" />
                  <span>
                    {step.podConfig?.cpu ?? "auto"} · {step.podConfig?.memory ?? "auto"}
                  </span>
                </span>
              ) : null}
            </div>
          ) : null}

          {/* Parallel Dependencies Preview */}
          {step.dependsOn && step.dependsOn.length > 0 ? (
            <div className="text-xs text-accent-text/80 font-medium">
              ↳ Depends on {step.dependsOn.length} step{step.dependsOn.length > 1 ? "s" : ""}{" "}
              (Parallel Join)
            </div>
          ) : null}

          {/* Gate parameters preview */}
          {step.gate ? (
            <div className="rounded-md border border-border/60 bg-muted/30 p-2 space-y-1 text-xs text-muted-foreground w-full">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <Users className="size-3 text-foreground/70" aria-hidden="true" />
                  <span>{step.deciders.length} decider(s)</span>
                </span>
                {step.expiryMinutes ? (
                  <span className="flex items-center gap-1 tabular-nums">
                    <Clock className="size-3" aria-hidden="true" />
                    <span>{step.expiryMinutes}m</span>
                  </span>
                ) : null}
              </div>

              {/* Exact accessibility string required by tests: "rejects to Plan" */}
              <div className="flex items-center gap-1 text-danger pt-0.5">
                <CornerDownLeft className="size-3 shrink-0" aria-hidden="true" />
                <span className="truncate">
                  {step.rejectRoute
                    ? `rejects to ${rejectTarget?.name ?? "an unknown step"}`
                    : "No reject route"}
                </span>
              </div>
            </div>
          ) : null}

          {/* Inline Validation Issues */}
          {issues.length > 0 ? (
            <div className="mt-1 space-y-1 w-full">
              {issues.map((issue) => (
                <div
                  key={issue.rule}
                  role="alert"
                  className="flex items-start gap-1 rounded bg-danger/10 px-2 py-1 text-xs font-medium text-danger"
                >
                  <AlertTriangle className="size-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="leading-tight">{issue.message}</span>
                </div>
              ))}
            </div>
          ) : null}
        </button>
      </div>
    </div>
  );
}
